import MainLayout from "@/shared/components/MainLayout";
import Header from "@/shared/components/ui/Header";
import { AnimatePresence, motion } from "framer-motion";
import {
    History,
    ShieldCheck,
    TrendingUp,
    Truck,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Activities from "../Activities/Activities";
import AssignUserPrivileges from "../AssignUserPrivileges/AssignUserPrivileges";
import SalesProfits from "../SalesProfits/SalesProfits";
import ShippingZonesManagement from "../ShippingZonesManagement/ShippingZonesManagement";
import ItemSalesReport from "../ItemSalesReport/ItemSalesReport";



const REPORT_TABS = [
    {
        key: "sales",
        label: "sales_and_profits",
        fallbackLabel: "المبيعات والأرباح",
        icon: TrendingUp,
        Component: SalesProfits,
    },
    {
        key: "item_sales_report",
        label: "item_sales_report",
        fallbackLabel: "مبيعات الأصناف",
        icon: TrendingUp,
        Component: ItemSalesReport,
    },
    {
        key: "activity_log",
        label: "activity_log",
        fallbackLabel: "سجل الأنشطة",
        icon: History,
        Component: Activities,
    },
    {
        key: "permissions",
        label: "permissions_assignment",
        fallbackLabel: "إسناد الصلاحيات",
        icon: ShieldCheck,
        Component: AssignUserPrivileges,
    },
    {
        key: "shipping_zones",
        label: "shipping_zones",
        fallbackLabel: "مناطق الشحن",
        icon: Truck,
        Component: ShippingZonesManagement,
    },
] as const;

type ReportTabKey = (typeof REPORT_TABS)[number]["key"];

const DEFAULT_TAB: ReportTabKey = "sales";

export default function Reports() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const requestedTab = searchParams.get("tab");
    const activeTab: ReportTabKey = useMemo(() => {
        const isValid = REPORT_TABS.some((tab) => tab.key === requestedTab);
        return isValid ? (requestedTab as ReportTabKey) : DEFAULT_TAB;
    }, [requestedTab]);

    const activeTabConfig = REPORT_TABS.find((tab) => tab.key === activeTab) ?? REPORT_TABS[0];
    const ActiveComponent = activeTabConfig.Component;

    const handleTabChange = (key: ReportTabKey) => {
        if (key === activeTab) return;
        setSearchParams({ tab: key }, { replace: true });
    };

    return (
        <MainLayout>
            <Header title={t("reports", "التقارير")} />

            <main className="px-4 py-8 min-h-[calc(100vh-100px)] flex flex-col items-center justify-start">
                <div className="w-full max-w-7xl">
                    <div className="mb-6 w-full bg-white p-2 rounded-2xl border border-slate-200 shadow-sm">
                        <div className="flex flex-wrap items-center gap-1.5">
                            {REPORT_TABS.map(({ key, label, fallbackLabel, icon: Icon }) => {
                                const isActive = key === activeTab;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => handleTabChange(key)}
                                        aria-current={isActive ? "page" : undefined}
                                        className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors duration-200 cursor-pointer outline-none
                                            ${isActive
                                                ? "text-violet-700"
                                                : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                                            }`}
                                    >
                                        {isActive && (
                                            <motion.span
                                                layoutId="reports-active-tab-bg"
                                                className="absolute inset-0 bg-violet-50 border border-violet-200 rounded-xl"
                                                transition={{ type: "spring", stiffness: 500, damping: 35 }}
                                            />
                                        )}
                                        <Icon className={`relative w-4 h-4 shrink-0 ${isActive ? "text-violet-600" : "text-slate-400"}`} />
                                        <span className="relative whitespace-nowrap">{t(label, fallbackLabel)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.18 }}
                        >
                            <ActiveComponent />
                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>
        </MainLayout>
    );
}

export const buildReportTabPath = (tab: ReportTabKey = DEFAULT_TAB) => `/reports?tab=${tab}`;

export type { ReportTabKey };
